import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { extractRecipeSizeLabel } from "./recipeCatalogTags.js";

export {
  extractRecipeSizeLabel,
  extractRecipeStyleLabel,
  extractRecipeStyleLabels,
  normalizeSizeLabel,
  parseSizeList,
} from "./recipeCatalogTags.js";

export type GroupMemberSection = {
  kind: MfgRecipeKind;
  sizeLabel: string | null;
  recipes: MfgRecipeSummary[];
};

const KIND_ORDER: MfgRecipeKind[] = [
  "product",
  "package",
  "assembly",
  "variant",
  "option",
];

function kindRank(kind: MfgRecipeKind): number {
  const i = KIND_ORDER.indexOf(kind);
  return i >= 0 ? i : KIND_ORDER.length;
}

/** Members of a recipe group, ordered by kind then size (unknown sizes last). */
export function groupRecipeMembers(
  recipes: readonly MfgRecipeSummary[],
  recipeGroupId: string,
): GroupMemberSection[] {
  const members = recipes.filter((r) => r.recipe_group_id === recipeGroupId);
  const buckets = new Map<string, GroupMemberSection>();

  for (const recipe of members) {
    const sizeLabel = extractRecipeSizeLabel(recipe);
    const key = `${recipe.kind}::${sizeLabel ?? "__"}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.recipes.push(recipe);
    } else {
      buckets.set(key, { kind: recipe.kind, sizeLabel, recipes: [recipe] });
    }
  }

  const sections = [...buckets.values()];
  for (const section of sections) {
    section.recipes.sort((a, b) => a.code.localeCompare(b.code) || a.name.localeCompare(b.name));
  }
  sections.sort((a, b) => {
    const kr = kindRank(a.kind) - kindRank(b.kind);
    if (kr !== 0) return kr;
    if (a.sizeLabel === b.sizeLabel) return 0;
    if (!a.sizeLabel) return 1;
    if (!b.sizeLabel) return -1;
    return a.sizeLabel.localeCompare(b.sizeLabel, undefined, { numeric: true });
  });
  return sections;
}

export function productMemberIds(
  recipes: readonly MfgRecipeSummary[],
  recipeGroupId: string,
): string[] {
  return recipes
    .filter((r) => r.recipe_group_id === recipeGroupId && r.kind === "product" && r.is_active)
    .map((r) => r.id);
}
